import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModuleLandingCompact, ContentWrapper, FavoritesAggregator } from '@textile-erp/ui';
import { Star } from 'lucide-react';

export function PinnedLanding() {
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);

  // Get aggregated favorites from all modules
  const categories = useMemo(() => {
    // TODO: Get actual userId from auth when available
    const userId = undefined;
    return FavoritesAggregator.getAllFavoritesByModule(userId);
  }, [refreshKey]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleFavoriteToggle = (path: string, isFavorite: boolean) => {
    // Find which module this path belongs to
    const moduleKey = inferModuleFromPath(path);
    if (!moduleKey) return;

    // Use the individual module's favorites hook to toggle
    const storageKey = `textile-erp-favorites-${moduleKey}`;
    try {
      const existing = localStorage.getItem(storageKey);
      const favorites: string[] = existing ? JSON.parse(existing) : [];
      
      if (isFavorite) {
        // Add to favorites
        if (!favorites.includes(path)) {
          favorites.push(path);
          localStorage.setItem(storageKey, JSON.stringify(favorites));
        }
      } else {
        // Remove from favorites
        const updatedFavorites = favorites.filter(fav => fav !== path);
        localStorage.setItem(storageKey, JSON.stringify(updatedFavorites));
      }
      
      // Refresh the categories to reflect changes
      setRefreshKey(prev => prev + 1);
      
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };


  // Helper function to infer module from path
  const inferModuleFromPath = (path: string): string | null => {
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    
    // Convert first segment to module key format
    return segments[0].toLowerCase().replace(/[_\s]+/g, '-');
  };

  const totalFavorites = categories.reduce((total, cat) => total + cat.items.length, 0);

  return (
    <ContentWrapper
      breadcrumbs={[
        { label: 'Dashboard', onClick: () => navigate('/') },
        { label: 'Pinned Items' }
      ]}
      noPadding={true}
    >
      <div className="h-full">
        {totalFavorites > 0 ? (
          <ModuleLandingCompact
            title="Pinned Items"
            description={`Your ${totalFavorites} favorite items organized by module`}
            categories={categories}
            onNavigate={handleNavigation}
            showSearch={true}
            showViewToggle={true}
            showExpandCollapseAll={true}
            showFavorites={false} // Hide favorites since everything is already favorited
            initialFavorites={[]}
            onFavoriteToggle={handleFavoriteToggle}
            defaultView="compact"
            maxColumns={3}
            className="h-full"
          />
        ) : (
          <EmptyState onNavigate={navigate} />
        )}
      </div>
    </ContentWrapper>
  );
}

// Empty state when no favorites exist
function EmptyState({ onNavigate }: { onNavigate: (path: string) => void }) {
  const suggestedPages = [
    { label: 'User Management', path: '/users', description: 'Manage user accounts and permissions' },
    { label: 'Identity & Access', path: '/identity', description: 'Roles and permission management' },
    { label: 'UI Examples', path: '/ui-examples', description: 'Explore available UI components' }
  ];

  return (
    <div className="h-full flex items-center justify-center">
      <div className="max-w-md mx-auto text-center p-8">
        <div className="w-16 h-16 mx-auto mb-6 bg-primary-50 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
          <Star className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          No Pinned Items Yet
        </h2>
        
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Start exploring modules and pin your favorite items for quick access. 
          Click the star icon next to any menu item to add it here.
        </p>

        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Popular sections to explore:
          </p>
          
          {suggestedPages.map((page, index) => (
            <button
              key={index}
              onClick={() => onNavigate(page.path)}
              className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-all group"
            >
              <div className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">
                {page.label}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {page.description}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}